import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, UserPlus, UserCheck } from "lucide-react";
import Avatar from "@/components/Avatar";
import {
  searchPeople,
  follow,
  unfollow,
  listFollowingIds,
} from "@/lib/social";

/**
 * PeopleResults — inline autocomplete-style list of profile matches for the
 * feed's search input. Renders only when `query` has at least 2 chars.
 *
 *   • Queries Supabase via `searchPeople(query)` with 250ms debounce.
 *   • Each row links to the public profile and exposes a Follow / Following
 *     toggle. Optimistic update with rollback on error.
 *   • Skips the current viewer from results so they don't see themselves.
 *
 * Visual language matches the chunky/cream feed (rounded card, ink borders,
 * lime accent on the Follow CTA on hover).
 */
export default function PeopleResults({ query, viewerId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [followingSet, setFollowingSet] = useState(() => new Set());
  const [pending, setPending] = useState(() => new Set());

  // Initial fetch: which people does the viewer already follow? Used to
  // render the correct CTA label without a per-row roundtrip.
  useEffect(() => {
    if (!viewerId) {
      setFollowingSet(new Set());
      return;
    }
    let cancelled = false;
    listFollowingIds(viewerId)
      .then((ids) => { if (!cancelled) setFollowingSet(new Set(ids)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [viewerId]);

  // Debounced people search.
  useEffect(() => {
    const q = String(query || "").trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const rows = await searchPeople(q, { signal: ctrl.signal });
        // Filter the viewer out so they don't try to follow themselves.
        setResults(rows.filter((r) => r.id !== viewerId));
      } catch (err) {
        if (err?.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query, viewerId]);

  const onToggle = async (p) => {
    if (!viewerId) {
      toast.message("Log in to follow people.");
      return;
    }
    if (pending.has(p.id)) return;
    const wasFollowing = followingSet.has(p.id);
    // Optimistic flip.
    setFollowingSet((s) => {
      const n = new Set(s);
      if (wasFollowing) n.delete(p.id); else n.add(p.id);
      return n;
    });
    setPending((s) => new Set(s).add(p.id));
    try {
      if (wasFollowing) {
        await unfollow(viewerId, p.id);
        toast.message(`Unfollowed @${p.username || "user"}.`);
      } else {
        await follow(viewerId, p.id);
        toast.success(`Following @${p.username || "user"}.`);
      }
    } catch {
      // Rollback.
      setFollowingSet((s) => {
        const n = new Set(s);
        if (wasFollowing) n.add(p.id); else n.delete(p.id);
        return n;
      });
      toast.error("Couldn't update follow.");
    } finally {
      setPending((s) => {
        const n = new Set(s); n.delete(p.id); return n;
      });
    }
  };

  if (String(query || "").trim().length < 2) return null;

  return (
    <div
      data-testid="feed-people-results"
      className="mb-4 border-[2.5px] border-ink rounded-2xl bg-paper overflow-hidden shadow-pop-sm"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b-[2.5px] border-ink">
        <span className="text-[10px] uppercase tracking-[0.28em] font-extrabold text-ink/70">
          People
        </span>
        {loading && <Loader2 size={14} className="animate-spin text-ink/55" />}
      </div>

      {!loading && results.length === 0 ? (
        <div
          data-testid="feed-people-empty"
          className="px-4 py-6 text-center text-[12px] uppercase tracking-widest text-ink/45"
        >
          No people match "{query}"
        </div>
      ) : (
        <ul className="divide-y divide-ink/10">
          {results.map((p) => {
            const isFollowing = followingSet.has(p.id);
            const isPending = pending.has(p.id);
            return (
              <li
                key={p.id}
                data-testid={`feed-people-row-${p.username || p.id}`}
                className="flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3"
              >
                <Link
                  to={`/u/${p.username || p.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 group"
                  data-testid={`feed-people-link-${p.username || p.id}`}
                >
                  <Avatar src={p.avatar_url} name={p.display_name || p.username} size={40} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-bold text-ink truncate group-hover:underline underline-offset-4">
                      {p.display_name || p.full_name || p.username}
                      {p.is_verified_admin && (
                        <ShieldCheck size={13} strokeWidth={2.6} className="text-lime-600 shrink-0" />
                      )}
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-ink/55 truncate">
                      {p.username ? `@${p.username}` : "user"}
                      {p.city ? ` · ${p.city}` : ""}
                    </div>
                  </div>
                </Link>

                <button
                  type="button"
                  onClick={() => onToggle(p)}
                  disabled={isPending}
                  data-testid={`feed-people-follow-${p.username || p.id}`}
                  aria-pressed={isFollowing}
                  className={
                    "shrink-0 inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest font-extrabold transition-colors " +
                    (isFollowing
                      ? "bg-ink text-paper hover:bg-paper hover:text-ink"
                      : "bg-paper text-ink hover:bg-lime")
                  }
                >
                  {isPending ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : isFollowing ? (
                    <UserCheck size={12} strokeWidth={2.8} />
                  ) : (
                    <UserPlus size={12} strokeWidth={2.8} />
                  )}
                  {isFollowing ? "Following" : "Follow"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
